import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import PageLoader from './components/PageLoader';

const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Users = lazy(() => import('./pages/Users'));
const Banners = lazy(() => import('./pages/Banners'));
const CmsPages = lazy(() => import('./pages/CmsPages'));
const AboutUsBuilder = lazy(() => import('./pages/AboutUsBuilder'));
const Guides = lazy(() => import('./pages/Guides'));
const MetalTypes = lazy(() => import('./pages/MetalTypes'));
const Categories = lazy(() => import('./pages/Categories'));
const Products = lazy(() => import('./pages/Products'));
const StoneTypes = lazy(() => import('./pages/StoneTypes'));
const Carats = lazy(() => import('./pages/Carats'));
const Sizes = lazy(() => import('./pages/Sizes'));
const Occasions = lazy(() => import('./pages/Occasions'));
const Weights = lazy(() => import('./pages/Weights'));
const Genders = lazy(() => import('./pages/Genders'));
const MetalPrices = lazy(() => import('./pages/MetalPrices'));
const SocialLoginSettings = lazy(() => import('./pages/SocialLoginSettings'));
const ElitePlans = lazy(() => import('./pages/ElitePlans'));
const PGMVideos = lazy(() => import('./pages/PGMVideos'));
const DiamondCategories = lazy(() => import('./pages/DiamondCategories'));
const DiamondPrices = lazy(() => import('./pages/DiamondPrices'));
const DiamondCentWeights = lazy(() => import('./pages/DiamondCentWeights'));
const PriceManagement = lazy(() => import('./pages/PriceManagement'));
const ScheduleCalls = lazy(() => import('./pages/ScheduleCalls'));
const DiamondMetalTypes = lazy(() => import('./pages/DiamondMetalTypes'));
const PromoCodes = lazy(() => import('./pages/PromoCodes'));
const PeakCollections = lazy(() => import('./pages/PeakCollections'));
const PeakCollectionForm = lazy(() => import('./pages/PeakCollectionForm'));
const EliteMembers = lazy(() => import('./pages/EliteMembers'));
const ProductOrders = lazy(() => import('./pages/ProductOrders'));
const OrderItems = lazy(() => import('./pages/OrderItems'));

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="min-h-screen bg-background">
          <Routes>
            {/* Public routes */}
            <Route
              path="/login"
              element={
                <Suspense fallback={<PageLoader />}>
                  <Login />
                </Suspense>
              }
            />
            <Route
              path="/register"
              element={
                <Suspense fallback={<PageLoader />}>
                  <Register />
                </Suspense>
              }
            />

            {/* Protected routes */}
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Suspense fallback={<PageLoader />}>
                      <Routes>
                        <Route path="/" element={<Navigate to="/dashboard" replace />} />
                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="/users" element={<Users />} />
                        <Route path="/banners" element={<Banners />} />
                        <Route path="/cms-pages" element={<CmsPages />} />
                        <Route path="/about-us-builder" element={<AboutUsBuilder />} />
                        <Route path="/guides" element={<Guides />} />
                        <Route path="/metal-types" element={<MetalTypes />} />
                        <Route path="/categories" element={<Categories />} />
                        <Route path="/stone-types" element={<StoneTypes />} />
                        <Route path="/carats" element={<Carats />} />
                        <Route path="/sizes" element={<Sizes />} />
                        <Route path="/occasions" element={<Occasions />} />
                        <Route path="/weights" element={<Weights />} />
                        <Route path="/genders" element={<Genders />} />
                        <Route path="/products" element={<Products />} />
                        <Route path="/metal-prices" element={<MetalPrices />} />
                        <Route path="/elite-plans" element={<ElitePlans />} />
                        <Route path="/pgm-videos" element={<PGMVideos />} />
                        <Route path="/diamond-categories" element={<DiamondCategories />} />
                        <Route path="/diamond-prices" element={<DiamondPrices />} />
                        <Route path="/diamond-cent-weights" element={<DiamondCentWeights />} />
                        <Route path="/diamond-metal-types" element={<DiamondMetalTypes />} />
                        <Route path="/promocodes" element={<PromoCodes />} />
                        <Route path="/price-management" element={<PriceManagement />} />
                        <Route path="/schedule-calls" element={<ScheduleCalls />} />
                        <Route path="/social-login-settings" element={<SocialLoginSettings />} />
                        <Route path="/peak-collections" element={<PeakCollections />} />
                        <Route path="/peak-collections/new" element={<PeakCollectionForm />} />
                        <Route path="/peak-collections/edit/:id" element={<PeakCollectionForm />} />
                        <Route path="/elite-members" element={<EliteMembers />} />
                        <Route path="/product-orders" element={<ProductOrders />} />
                        <Route path="/order-items" element={<OrderItems />} />
                        <Route path="*" element={<Navigate to="/dashboard" replace />} />
                      </Routes>
                    </Suspense>
                  </Layout>
                </ProtectedRoute>
              }
            />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
